import { useState } from 'react';

import { Select } from 'antd';
import { useGetUserListInfiniteInfiniteQuery, useGetUserQuery } from 'services';
import useDebounce from 'hooks/useDebounce';

/**
 * Bir role sahip kullanıcılar arasından seçim yapan, aranabilir dropdown.
 *
 * Kullanıcılar ad sırasıyla, infinite scroll ile sayfa sayfa çekilir (`itemsPerPage: 20`).
 * Arama server-side yapılır (`searchKey`: ad + soyad + e-posta, debounce'lu).
 *
 * Seçili kullanıcı yüklenen sayfalarda olmayabileceğinden tek kayıtlık istekle çekilip
 * listeye iğnelenir; aksi halde antd Select etiket yerine ham id gösterirdi.
 *
 * @param {Object} props
 * @param {string} props.role - Listelenecek kullanıcıların rolü (`roles[]` filtresi).
 * @param {number} [props.value] - Seçili kullanıcının id'si. Form.Item enjekte eder.
 * @param {(value: number) => void} [props.onChange] - Seçim değişiminde çağrılır. Form.Item enjekte eder.
 * @param {boolean} [props.disabled=false] - Alanı devre dışı bırakır.
 */
const UserSelect = ({ role, value, onChange, disabled = false }) => {
  const [searchText, setSearchText] = useState('');
  const { handleDebounce, cancelDebounce } = useDebounce();

  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } = useGetUserListInfiniteInfiniteQuery({
    role,
    search: searchText,
  });
  const { data: selectedUser } = useGetUserQuery(value, { skip: !value });

  const rawUsers = (data?.pages ?? []).flatMap((page) => page['hydra:member']);
  if (selectedUser) {
    rawUsers.unshift(selectedUser);
  }

  // Sayfalar arası tekrar eden kayıtları ve iğnelenen seçili kullanıcıyı id bazında tekilleştiriyoruz.
  const users = Array.from(new Map(rawUsers.map((user) => [user.id, user])).values());

  const handleSearch = (input) => {
    handleDebounce({
      callback: () => {
        setSearchText(input);
      },
      delay: 500,
    });
  };

  // Dropdown kapanınca antd arama metnini siler; listenin de filtresiz hale dönmesi için aramayı sıfırlıyoruz.
  const handleDropdownVisibleChange = (open) => {
    if (!open) {
      cancelDebounce();
      setSearchText('');
    }
  };

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20 && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      options={users.map((user) => ({
        label: user.fullName,
        value: user.id,
      }))}
      showSearch
      filterOption={false}
      onSearch={handleSearch}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onPopupScroll={handlePopupScroll}
      loading={isFetching}
      disabled={disabled}
      className="gx-w-100"
    />
  );
};

export default UserSelect;
